import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DialogWithTitle from '../DialogWithTitle';
import { useTranslation, withTranslation } from '../../src/i18n';
import { colors, fontSize, border } from '../../constants/styleConstants';
import LoginFields from '../LoginFields';
import { TP } from '../../constants/consts';
import route from '../../constants/routes';
import SocialSignInButtons from '../SocialSignInButtons';
import Logo from '../Logo';
import { selectLoginErrorMessage, selectLoginLoadingState } from '../../containers/App/selectors';

const useStyles = makeStyles(() => ({
  loginDialog: {
    '& .MuiDialog-paperScrollPaper': {
      maxWidth: '347px',
      minWidth: '347px',
    },
    '& .MuiDialog-paperFullScreen': {
      maxWidth: '100% !important',
    },
  },
  contentClass: {
    padding: '20px',
    background: '#fafafa',
  },
  footer: {
    fontSize: fontSize.textFieldLabel,
    color: colors.lightBlack,
    marginTop: 12,
    textAlign: 'center',
    '& a': {
      fontWeight: 'bold',
      color: colors.blueLight,
    },
  },
  title: {
    fontSize: fontSize.heading,
    color: '#464646',
    fontFamily: 'PlayfairDisplay',
    textAlign: 'center',
    marginTop: '2px',
    margin: 0,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    fontSize: fontSize.medium,
    color: colors.lightBlack,
    margin: '8px 0 20px 0',
    '&::before': {
      content: "''",
      flex: 1,
      borderBottom: border.lightGrey,
      marginRight: '.25em',
    },
    '&::after': {
      content: "''",
      flex: 1,
      borderBottom: border.lightGrey,
      marginLeft: '.25em',
    },
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 32,
  },
  logo: {
    width: 125,
    height: 12,
  },
  errorMessage: {
    display: 'flex',
    fontSize: fontSize.smallTitle,
    color: colors.red,
    justifyContent: 'center',
    marginTop: 10,
  },
  message: {
    color: '#464646',
    fontSize: fontSize.description,
    fontWeight: 300,
    margin: 0,
    marginBottom: 16,
    textAlign: 'center',
  },
}));

const LoginDialog = ({ isOpen, onClose, url, isLoginLoading, error, message }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <DialogWithTitle
      className={classes.loginDialog}
      contentClass={classes.contentClass}
      open={isOpen}
      onClose={onClose}
      fullScreen={fullScreen}
      closeButton={fullScreen}
    >
      <>
        <p className={classes.title}>{t(`${TP}.m_LOGIN`)}</p>
        {message && <p className={classes.message}>{message}</p>}
        <SocialSignInButtons type={t(`${TP}.CONTINUE_WITH`)} />
        <div className={classes.divider}>{t(`${TP}.USE_YOUR_EMAIL`)}</div>
        <LoginFields url={url} isLoginLoading={isLoginLoading}></LoginFields>
        <div>{error && <span className={classes.errorMessage}>{error}</span>}</div>
        <div className={classes.footer}>
          {t(`${TP}.NO_ACCOUNT`)} &nbsp;
          <a href={route.REGISTER}>{t(`${TP}.CREATE_ONE`)}</a>
        </div>
        {fullScreen && (
          <div className={classes.logoContainer}>
            <Logo className={classes.logo} />
          </div>
        )}
      </>
    </DialogWithTitle>
  );
};

const mapStateToProps = createStructuredSelector({
  isLoginLoading: selectLoginLoadingState(),
  error: selectLoginErrorMessage(),
});
const withConnect = connect(mapStateToProps, null);

LoginDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  url: PropTypes.object,
  error: PropTypes.string,
  isLoginLoading: PropTypes.bool,
  message: PropTypes.string,
};

export default compose(withConnect, withTranslation())(LoginDialog);
